import { formatMoney } from '@travelata-libs/utils';
import { toLngLat } from './mercator';
import { MARKER_Z_CLUSTER, MARKER_Z_PIN, MARKER_Z_SELECTED } from '../config/map';
import { HOTELS_MAP_TEXTS } from '../config/texts';
import type { MapCluster, MapMarkerKind, MapMarkerView, MarkerDiff } from '../model/types';

const WHITESPACE_RE = /\s/g;
const NBSP = '\u00A0';
const RU_NUMBER_FORMAT = new Intl.NumberFormat('ru-RU');

export function formatMapPrice(price: number | null): string {
  if (price === null) return HOTELS_MAP_TEXTS.priceEmpty;

  const amount = formatMoney(price).replaceAll(WHITESPACE_RE, NBSP);

  return `${HOTELS_MAP_TEXTS.priceFrom} ${amount}`;
}

export function formatMapCount(count: number): string {
  return RU_NUMBER_FORMAT.format(count).replaceAll(WHITESPACE_RE, NBSP);
}

export function toMarkerViews(clusters: readonly MapCluster[], selectedHotelId: number | null): MapMarkerView[] {
  return clusters.map((cluster) => {
    const count = cluster.hotels.length;
    const kind: MapMarkerKind = count > 1 ? 'cluster' : 'pin';
    const hotelIds = cluster.hotels.map((hotel) => hotel.hotelId);
    const containsSelected = selectedHotelId !== null && hotelIds.includes(selectedHotelId);
    const selected = kind === 'pin' && containsSelected;

    return {
      key: cluster.key,
      kind,
      coordinates: toLngLat(cluster.anchor),
      countLabel: formatMapCount(count),
      priceLabel: kind === 'pin' ? formatMapPrice(cluster.minPrice) : '',
      count,
      hotelIds,
      selected,
      containsSelected,
      zIndex: getMarkerZIndex(kind, selected),
      testid: `hotels-map-marker-${cluster.key}`,
    };
  });
}

export function diffMarkerViews(prev: readonly MapMarkerView[], next: readonly MapMarkerView[]): MarkerDiff {
  const previousByKey = toViewsByKey(prev);
  const nextByKey = toViewsByKey(next);

  const added: MapMarkerView[] = [];
  const updated: MapMarkerView[] = [];

  next.forEach((view) => {
    const previous = previousByKey.get(view.key);

    if (!previous) {
      added.push(view);

      return;
    }

    if (isMarkerViewChanged(previous, view)) updated.push(view);
  });

  const removed = prev.filter((view) => !nextByKey.has(view.key)).map((view) => view.key);

  return { added, updated, removed };
}

function toViewsByKey(views: readonly MapMarkerView[]): Map<number, MapMarkerView> {
  return new Map(views.map((view): [number, MapMarkerView] => [view.key, view]));
}

function isMarkerViewChanged(previous: MapMarkerView, next: MapMarkerView): boolean {
  return (
    previous.coordinates[0] !== next.coordinates[0] ||
    previous.coordinates[1] !== next.coordinates[1] ||
    previous.countLabel !== next.countLabel ||
    previous.priceLabel !== next.priceLabel ||
    previous.count !== next.count ||
    previous.selected !== next.selected ||
    previous.containsSelected !== next.containsSelected ||
    previous.zIndex !== next.zIndex
  );
}

function getMarkerZIndex(kind: MapMarkerKind, selected: boolean): number {
  if (selected) return MARKER_Z_SELECTED;

  return kind === 'cluster' ? MARKER_Z_CLUSTER : MARKER_Z_PIN;
}
